<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import EventBus from '../../../../event-bus/EventBus'
import { useAdaMoltenBundle } from '../../../../composables/use-ada-molten-bundle'
import { preventNonPositiveNumberKeys } from '../../../../utils/validation/number'
import { useDeviceDetection } from '../../../../composables/use-device-detection'
import { useConfigStore } from '../../../../stores/config'

type PowerUnit = 'ps' | 'kw' | 'hp'
type SelectableUnit = 'PS' | 'kW' | 'hp'
type ConversionResult = { unit: PowerUnit, value: string }

const multiplier: Record<PowerUnit, { unit: PowerUnit, factor: number }[]> = {
  ps: [{ unit: 'kw', factor: 0.735499 }, { unit: 'hp', factor: 0.986320 }],
  kw: [{ unit: 'ps', factor: 1.35962 }, { unit: 'hp', factor: 1.34102 }],
  hp: [{ unit: 'ps', factor: 1.01387 }, { unit: 'kw', factor: 0.7457 }]
}

const unitKeys: Record<SelectableUnit, PowerUnit> = {
  PS: 'ps',
  kW: 'kw',
  hp: 'hp'
}

const renderUnits: Record<PowerUnit, SelectableUnit> = {
  ps: 'PS',
  kw: 'kW',
  hp: 'hp'
}

const configStore = useConfigStore()
const { deviceType } = useDeviceDetection()
const registerSlots = ref<string[]>([])
const { adaSlotReloadOnInteraction } = useAdaMoltenBundle({ registerSlots, deviceType })

const value = ref<number | null>(null)
const unit = ref<SelectableUnit>('PS')
const results = ref<ConversionResult[]>([])
const errorMsg = ref<string | null>(null)

const reloadConfig = computed(() => ({
  ...configStore.rsConfig.adSlotUsage?.reloadAdsOnInteraction?.config,
  minDisplayDuration: 0,
  clickCount: 1
}))
const stringValue = computed(() => String(value.value || ''))
const splitValues = computed(() => stringValue.value.split(/[.,]/))
const commaSeparatedValue = computed(() => stringValue.value.replace('.', ','))

watch(unit, () => {
  value.value = null
  results.value = []
})

watch(value, () => {
  errorMsg.value = null
  results.value = []
})

const getValidationError = () => {
  if ((stringValue.value.length === 0 || value.value === null) && value.value !== 0) {
    return 'Der Ausgangswert muss zur Berechnung eingegeben werden.'
  }
  if (splitValues.value[0].length > 4) return 'Es werden nur Ausgangswerte mit maximal vier Stellen unterstützt.'
  if ((value.value ?? 0) <= 0) return 'Der Ausgangswert muss eine positive Zahl sein.'
  if (splitValues.value[1]?.length > 2) return 'Es werden nur Ausgangswerte mit maximal zwei Nachkommastellen unterstützt.'
  return null
}

const validForm = () => {
  errorMsg.value = getValidationError()
  return !errorMsg.value
}

const convert = (reloadAds = false) => {
  if (!validForm()) return

  results.value = multiplier[unitKeys[unit.value]].map(({ unit: targetUnit, factor }) => ({
    unit: targetUnit,
    value: ((value.value ?? 0) * factor).toFixed(2).replace('.', ',')
  }))

  if (reloadAds) {
    EventBus.$emit('triggerAGF')
    adaSlotReloadOnInteraction(reloadConfig.value)
  }
}
</script>

<template>
  <div class="bx-content bx-typo--form bx-typo--form-contact">
    <form class="bx-form bx-form--pur bx-form--pwr-converter"
          novalidate
          @submit.prevent="convert(true)">
      <div class="bx-form__checkbox-wrapper">
        <div class="bx-content__interline bx-typo--content-interline">
          Umrechnungsart
        </div>
        <div>
          <label for="ps"
                 class="bx-form__label">PS nach kW, hp</label>
          <input id="ps"
                 v-model="unit"
                 type="radio"
                 value="PS"
                 checked>
        </div>
        <div>
          <label for="kw"
                 class="bx-form__label">kW nach PS, hp</label>
          <input id="kw"
                 v-model="unit"
                 type="radio"
                 value="kW">
        </div>
        <div>
          <label for="hp"
                 class="bx-form__label">hp nach kW, PS</label>
          <input id="hp"
                 v-model="unit"
                 type="radio"
                 value="hp">
        </div>
      </div>
      <div class="bx-form__input-wrapper"
           :class="{'bx-has-error': errorMsg}">
        <div class="bx-content__interline bx-typo--content-interline">
          Ausgangswert
        </div>
        <label for="value"
               class="bx-form__label">Leistung in {{ unit }}
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">*
          </span>
        </label>
        <input id="value"
               v-model="value"
               type="number"
               placeholder="z.B. 100"
               @keydown="preventNonPositiveNumberKeys">
        <span v-if="errorMsg"
              class="bx-form__message bx-form__message--error">
          {{ errorMsg }}
        </span>
      </div>
      <button type="submit"
              class="bx-form__button">
        Umrechnen
      </button>
      <div class="bx-form__disclaimer-required">
        * Pflichtfeld
      </div>
      <div v-if="stringValue.length && results.length && !errorMsg"
           class="bx-affiliate-link bx-form__result bx-typo--content-interline">
        <span><strong>{{ commaSeparatedValue }} {{ unit }}</strong> entsprechen</span>
        <br><strong>{{ results[0].value }} {{ renderUnits[results[0].unit] }}</strong>
        bzw. <strong>{{ results[1].value }} {{ renderUnits[results[1].unit] }}</strong>
      </div>
    </form>
  </div>
</template>
